import React, { useMemo } from "react";
import { ButtonControl, FieldControl, IconControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useVisualFormContext } from "../../../contexts/VisualFormContext";
import { normalizeDashboardQueryState } from "../../../visualQueryStateUtils";
import {
  buildVisualAggregateDraft,
  getCompatibleVisualAggregationOptions,
  VISUAL_AGGREGATION_OPTIONS,
} from "../../../visualDesignUtils";
import "./Grouping.scss";

const GROUPED_QUERY_MODE = 2;
const GROUPED_SEMANTIC_SHAPE = 2;
const RELATION_QUERY_MODE = 1;
const DETAIL_SEMANTIC_SHAPE = 1;
const VIEW_MODEL_KIND = 2;

const normalizeString = (value) => String(value ?? "").trim();

const sortOptions = (items = []) =>
  [...items].sort((left, right) =>
    String(left?.Text ?? "").localeCompare(String(right?.Text ?? ""))
  );

const dedupeByValue = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = normalizeString(item?.Value).toLowerCase();
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getAggregateIdentity = (aggregate) =>
  `${normalizeString(aggregate?.Column).toLowerCase()}::${Number(aggregate?.AggregationKind ?? 0)}`;

const filterVisualAggregatesByGroupedColumns = (aggregates = [], groupedColumns = []) => {
  const groupedColumnKeys = new Set(
    (Array.isArray(groupedColumns) ? groupedColumns : [])
      .map((value) => normalizeString(value).toLowerCase())
      .filter(Boolean)
  );

  return (Array.isArray(aggregates) ? aggregates : [])
    .filter((aggregate) => !groupedColumnKeys.has(normalizeString(aggregate?.Column).toLowerCase()))
    .map((aggregate, index) => ({
      ...aggregate,
      Priority: index,
    }));
};

const buildColumnOptions = (columns = [], predicate = null) =>
  dedupeByValue(
    columns
      .filter((column) => (predicate ? predicate(column) : true))
      .map((column) => ({
        Text: column?.DisplayName || column?.ColumnName || column?.FieldName,
        Value: column?.FieldName || column?.ColumnName,
      }))
  );

const buildGroupedFieldOptions = (columns = [], measures = []) => {
  const columnOptions = columns
    .filter((column) => column?.CanGroup !== false && column?.IsVisible !== false)
    .map((column) => ({
      Text: `${column?.DisplayName || column?.ColumnName || column?.FieldName} (Column)`,
      Value: `column::${column?.FieldName || column?.ColumnName}`,
    }));
  const measureOptions = measures
    .filter((measure) => measure?.IsVisible !== false)
    .map((measure) => ({
      Text: `${measure?.DisplayName || measure?.MeasureName || measure?.FieldName} (Measure)`,
      Value: `measure::${measure?.MeasureName || measure?.FieldName}`,
    }));

  return dedupeByValue([...columnOptions, ...measureOptions]);
};

const findColumnByFieldName = (columns = [], fieldName) =>
  columns.find((column) => normalizeString(column?.FieldName).toLowerCase() === normalizeString(fieldName).toLowerCase()) ?? null;

const createVisualAggregateDraft = (columns = [], existingAggregates = []) => {
  const existingKeys = new Set(existingAggregates.map((aggregate) => getAggregateIdentity(aggregate)));

  for (const column of columns) {
    const compatibleOptions = getCompatibleVisualAggregationOptions(column);
    for (const option of compatibleOptions) {
      const candidate = buildVisualAggregateDraft(column, option?.Value);
      if (candidate && !existingKeys.has(getAggregateIdentity(candidate))) {
        return candidate;
      }
    }
  }

  return null;
};

export const Grouping = ({ selectedModelColumns = [], selectedModelMeasures = [] }) => {
  const { visualObject, setVisualObject } = useVisualFormContext();
  const queryState = visualObject?.DefaultQueryState ?? {};
  const supportsRealTime = !!queryState?.SupportsRealTime;
  const isViewModel = Number(visualObject?.ModelKind ?? 0) === VIEW_MODEL_KIND;
  const groupByColumns = useMemo(
    () => (Array.isArray(queryState?.GroupByColumns) ? queryState.GroupByColumns : []),
    [queryState?.GroupByColumns]
  );
  const measureNames = useMemo(
    () => (Array.isArray(queryState?.MeasureNames) ? queryState.MeasureNames : []),
    [queryState?.MeasureNames]
  );
  const visualAggregates = useMemo(
    () => (Array.isArray(queryState?.VisualAggregates) ? queryState.VisualAggregates : []),
    [queryState?.VisualAggregates]
  );

  const groupedFieldOptions = useMemo(
    () => sortOptions(buildGroupedFieldOptions(selectedModelColumns, selectedModelMeasures)),
    [selectedModelColumns, selectedModelMeasures]
  );
  const groupedFieldValues = useMemo(
    () => ([
      ...groupByColumns.map((value) => `column::${value}`),
      ...measureNames.map((value) => `measure::${value}`),
    ]),
    [groupByColumns, measureNames]
  );
  const aggregateColumnOptions = useMemo(
    () => sortOptions(buildColumnOptions(
      selectedModelColumns,
      (column) =>
        column?.IsVisible !== false
        && getCompatibleVisualAggregationOptions(column).length > 0
        && !groupByColumns.some((fieldName) => normalizeString(fieldName).toLowerCase() === normalizeString(column?.FieldName ?? column?.ColumnName).toLowerCase())
    )),
    [groupByColumns, selectedModelColumns]
  );

  const updateQueryState = (updater) => {
    setVisualObject((prev) => {
      const modelKind = Number(prev?.ModelKind ?? 0);
      const modelId = prev?.ModelId ?? "";
      const currentState = normalizeDashboardQueryState(prev?.DefaultQueryState, {
        ModelKind: modelKind,
        ModelId: modelId,
      });
      const nextPartial = typeof updater === "function"
        ? updater(currentState)
        : updater;
      const nextState = normalizeDashboardQueryState(currentState, {
        ...(nextPartial ?? {}),
        ModelKind: modelKind,
        ModelId: modelId,
      });
      const hasGroupedQueryShape =
        (nextState?.GroupByColumns?.length ?? 0) > 0
        || (nextState?.MeasureNames?.length ?? 0) > 0
        || (nextState?.VisualAggregates?.length ?? 0) > 0;

      return {
        ...prev,
        DefaultQueryState: normalizeDashboardQueryState(nextState, {
          ModelKind: modelKind,
          ModelId: modelId,
          QueryMode: hasGroupedQueryShape ? GROUPED_QUERY_MODE : RELATION_QUERY_MODE,
          SemanticShape: hasGroupedQueryShape ? GROUPED_SEMANTIC_SHAPE : DETAIL_SEMANTIC_SHAPE,
        }),
      };
    });
  };

  const handleAddVisualAggregate = () => {
    const nextAggregate = createVisualAggregateDraft(selectedModelColumns, visualAggregates);
    if (!nextAggregate) {
      return;
    }

    updateQueryState((currentState) => ({
      ...currentState,
      VisualAggregates: [
        ...(Array.isArray(currentState?.VisualAggregates) ? currentState.VisualAggregates : []),
        {
          ...nextAggregate,
          Priority: visualAggregates.length,
        },
      ],
    }));
  };

  const handleVisualAggregateChange = (aggregateId, fieldName, value) => {
    updateQueryState((currentState) => {
      const nextAggregates = (Array.isArray(currentState?.VisualAggregates) ? currentState.VisualAggregates : [])
        .map((aggregate, index) => {
          const currentId = normalizeString(aggregate?.UniqueId ?? aggregate?._id);
          if (currentId !== aggregateId) {
            return {
              ...aggregate,
              Priority: index,
            };
          }

          const nextColumnFieldName = fieldName === "Column"
            ? normalizeString(value)
            : normalizeString(aggregate?.Column);
          const nextColumn = findColumnByFieldName(selectedModelColumns, nextColumnFieldName);
          const nextDraft = buildVisualAggregateDraft(
            nextColumn,
            fieldName === "AggregationKind" ? Number(value) : Number(aggregate?.AggregationKind),
            aggregate
          );

          if (!nextDraft) {
            return {
              ...aggregate,
              Priority: index,
            };
          }

          return {
            ...nextDraft,
            Priority: index,
          };
        });

      return {
        ...currentState,
        VisualAggregates: nextAggregates,
      };
    });
  };

  const handleRemoveVisualAggregate = (aggregateId) => {
    updateQueryState((currentState) => ({
      ...currentState,
      VisualAggregates: (Array.isArray(currentState?.VisualAggregates) ? currentState.VisualAggregates : [])
        .filter((aggregate) => normalizeString(aggregate?.UniqueId ?? aggregate?._id) !== aggregateId)
        .map((aggregate, index) => ({
          ...aggregate,
          Priority: index,
        })),
    }));
  };

  if (!visualObject?.ModelId) {
    return (
      <div className="dialog-description">
        Select a model first to configure grouped query fields.
      </div>
    );
  }

  return (
    <div className="visual-grouping-step">
      <div className="visual-grouping-step__info">
        {!isViewModel ? (
          <div className="visual-grouping-step__realtime-toggle">
            <FieldControl
              className="visual-grouping-step__realtime-field"
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.CheckBox,
                FieldName: "SupportsRealTime",
                DisplayName: supportsRealTime ? "Supports Real Time" : "Supports Real Time (Disabled)",
                Description: "Keep this visual on the raw detail dataset so it stays compatible with RealTime execution.",
              }}
              groupId={"visual-grouping"}
              value={supportsRealTime}
              onChange={(value) => {
                const nextSupportsRealTime = !!value;
                updateQueryState((currentState) => ({
                  ...currentState,
                  SupportsRealTime: nextSupportsRealTime,
                  GroupByColumns: nextSupportsRealTime
                    ? []
                    : (Array.isArray(currentState?.GroupByColumns) ? currentState.GroupByColumns : []),
                  MeasureNames: nextSupportsRealTime
                    ? []
                    : (Array.isArray(currentState?.MeasureNames) ? currentState.MeasureNames : []),
                  VisualAggregates: nextSupportsRealTime
                    ? []
                    : (Array.isArray(currentState?.VisualAggregates) ? currentState.VisualAggregates : []),
                  QueryMode: nextSupportsRealTime ? RELATION_QUERY_MODE : currentState?.QueryMode,
                  SemanticShape: nextSupportsRealTime ? DETAIL_SEMANTIC_SHAPE : currentState?.SemanticShape,
                }));
              }}
            />
          </div>
        ) : null}
        <div className="visual-grouping-step__info-title">Optional Grouping</div>
        <ul className="visual-grouping-step__info-list">
          <li>If you leave grouped columns, measures, and visual aggregates empty, the visual stays on the raw detail dataset.</li>
          <li>Charts and other visual types can still aggregate the raw data within the chart.</li>
          <li>If you select grouped fields or visual aggregates here, those grouped outputs become the available fields in Design.</li>
        </ul>
      </div>

      <div className="visual-attribute">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.MultiSelectList,
            FieldName: "GroupedFields",
            DisplayName: "Grouped Columns and/or Measures",
            Description: "Select the semantic columns and measures to include in the grouped result.",
            IsRequired: false,
            DataJson: groupedFieldOptions,
          }}
          groupId={"visual-grouping"}
          value={groupedFieldValues}
          disabled={supportsRealTime}
          onChange={(value) => {
            const selectedValues = Array.isArray(value) ? value : [];
            const nextGroupByColumns = [];
            const nextMeasureNames = [];

            selectedValues.forEach((entry) => {
              const normalizedEntry = normalizeString(entry);
              if (!normalizedEntry) {
                return;
              }

              if (normalizedEntry.startsWith("column::")) {
                const fieldName = normalizedEntry.slice("column::".length);
                if (fieldName) {
                  nextGroupByColumns.push(fieldName);
                }
                return;
              }

              if (normalizedEntry.startsWith("measure::")) {
                const fieldName = normalizedEntry.slice("measure::".length);
                if (fieldName) {
                  nextMeasureNames.push(fieldName);
                }
              }
            });

            updateQueryState({
              SupportsRealTime: false,
              GroupByColumns: nextGroupByColumns,
              MeasureNames: nextMeasureNames,
              VisualAggregates: filterVisualAggregatesByGroupedColumns(visualAggregates, nextGroupByColumns),
            });
          }}
        />
      </div>

      <div className={`visual-grouping-step__section${supportsRealTime ? " visual-grouping-step__section--disabled" : ""}`}>
        <div className="visual-grouping-step__section-header">
          <div>
            <div className="visual-grouping-step__title">Visual Aggregates</div>
            <div className="visual-grouping-step__subtitle">
              Add one-off aggregates for this visual when a source column should be summarized without creating a reusable model measure.
            </div>
          </div>
          <span title="Add Aggregate">
            <ButtonControl
              className="visual-grouping-step__add-button"
              type="icon"
              onClick={handleAddVisualAggregate}
              disabled={supportsRealTime || aggregateColumnOptions.length === 0}
              title="Add Aggregate"
            >
              <IconControl iconKey="create" title="Add Aggregate" />
            </ButtonControl>
          </span>
        </div>

        {visualAggregates.length === 0 ? (
          <div className="dialog-description">
            No visual aggregates are configured yet.
          </div>
        ) : (
          <div className="visual-grouping-step__aggregate-list">
            {visualAggregates.map((aggregate) => {
              const aggregateId = normalizeString(aggregate?.UniqueId ?? aggregate?._id);
              const sourceColumn = findColumnByFieldName(selectedModelColumns, aggregate?.Column);
              const compatibleAggregationOptions = getCompatibleVisualAggregationOptions(sourceColumn);

              return (
                <div key={aggregateId} className="visual-grouping-step__aggregate-row">
                  <div className="visual-grouping-step__aggregate-fields">
                    <div className="visual-grouping-step__aggregate-field">
                      <FieldControl
                        fieldMetadata={{
                          FieldType: SystemTypes.UIFieldType.SearchSelectList,
                          FieldName: `VisualAggregateColumn-${aggregateId}`,
                          DisplayName: "Column",
                          Description: "Semantic column to aggregate for this visual.",
                          IsRequired: true,
                          DataJson: aggregateColumnOptions,
                        }}
                        groupId={`visual-grouping-${aggregateId}`}
                        value={aggregate?.Column ?? ""}
                        disabled={supportsRealTime}
                        onChange={(value) => handleVisualAggregateChange(aggregateId, "Column", value)}
                      />

                      <div className="visual-grouping-step__aggregate-details">
                        <span className="visual-grouping-step__aggregate-output-name">
                          {aggregate?.OutputName || "aggregate_output"}
                        </span>
                        <span className="visual-grouping-step__aggregate-output-separator">|</span>
                        <span className="visual-grouping-step__aggregate-output-label">
                          {aggregate?.DisplayName || "Aggregate output"}
                        </span>
                      </div>
                    </div>
                    <div className="visual-grouping-step__aggregate-field visual-grouping-step__aggregate-field--narrow visual-grouping-step__aggregate-field--actions">
                      <div className="visual-grouping-step__aggregate-action-row">
                        <div className="visual-grouping-step__aggregate-action-field">
                          <FieldControl
                            fieldMetadata={{
                              FieldType: SystemTypes.UIFieldType.SelectList,
                              FieldName: `VisualAggregateAggregation-${aggregateId}`,
                              DisplayName: "Aggregate",
                              Description: "Aggregate Summary Function",
                              IsRequired: true,
                              DataJson: compatibleAggregationOptions.length > 0 ? compatibleAggregationOptions : VISUAL_AGGREGATION_OPTIONS,
                            }}
                            groupId={`visual-grouping-${aggregateId}`}
                            value={aggregate?.AggregationKind ?? ""}
                            disabled={supportsRealTime}
                            onChange={(value) => handleVisualAggregateChange(aggregateId, "AggregationKind", value)}
                          />
                        </div>
                        <ButtonControl
                          type="icon-small"
                          className="visual-grouping-step__delete-button"
                          disabled={supportsRealTime}
                          onClick={() => handleRemoveVisualAggregate(aggregateId)}
                          title="Remove visual aggregate"
                        >
                          <IconControl iconKey="delete" />
                        </ButtonControl>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
